import React, { useContext, useState, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useAlert } from '../components/ui/alert/useAlert';
import { Settings as SettingsIcon, User, Bell, Shield, Info, Sun, Moon, Save, Eye, EyeOff, Lock, Mail, UserCircle, Download } from 'lucide-react';
import * as userProfileService from '../services/userProfileService';

export default function Settings() {
  const { user } = useContext(AuthContext);
  const { theme, setTheme } = useTheme();
  const { showSuccess, showError } = useAlert();
  
  // Stati locali per visualizzazione dinamica con refresh automatico
  const [displayUser, setDisplayUser] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    username: user?.username || ''
  });
  
  // Funzione per aggiornare l'utente nel localStorage e nello stato locale con refresh
  const updateLocalUser = (updatedUser) => {
    // Aggiorna il localStorage
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const mergedUser = { ...currentUser, ...updatedUser };
    localStorage.setItem('user', JSON.stringify(mergedUser));
    
    // Aggiorna lo stato locale immediatamente per refresh UI
    setDisplayUser(prev => ({
      ...prev,
      firstName: mergedUser.firstName || prev.firstName,
      lastName: mergedUser.lastName || prev.lastName,
      email: mergedUser.email || prev.email
    }));
    
    // Reset dei campi di modifica con i nuovi valori
    setFirstName(mergedUser.firstName || '');
    setLastName(mergedUser.lastName || '');
    setNewEmail(mergedUser.email || '');
  };

  // Stati per modifica nome
  const [editingName, setEditingName] = useState(false);
  const [firstName, setFirstName] = useState(user?.firstName || '');
  const [lastName, setLastName] = useState(user?.lastName || '');

  // Stati per modifica email
  const [editingEmail, setEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState(user?.email || '');
  const [emailPassword, setEmailPassword] = useState('');

  // Stati per modifica password
  const [editingPassword, setEditingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Stati UI
  const [loading, setLoading] = useState(false);

  // Carica dati iniziali e mantieni sincronizzati
  useEffect(() => {
    if (user) {
      const userData = {
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        email: user.email || '',
        username: user.username || ''
      };
      setDisplayUser(userData);
      setFirstName(userData.firstName);
      setLastName(userData.lastName);
      setNewEmail(userData.email);
    }
  }, [user]);

  // Funzioni di utilità
  const resetForm = () => {
    setFirstName(user?.firstName || '');
    setLastName(user?.lastName || '');
    setNewEmail(user?.email || '');
    setEmailPassword('');
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setEditingName(false);
    setEditingEmail(false);
    setEditingPassword(false);
  };

  // Handler per aggiornamento nome
  const handleUpdateName = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      showError('Nome e cognome sono obbligatori');
      return;
    }

    setLoading(true);
    try {
      const response = await userProfileService.updateName(firstName.trim(), lastName.trim());
      updateLocalUser(response.user);
      showSuccess('Nome aggiornato con successo');
      setEditingName(false);
    } catch (error) {
      showError(error.message || 'Errore nell\'aggiornamento del nome');
    } finally {
      setLoading(false);
    }
  };

  // Handler per aggiornamento email
  const handleUpdateEmail = async () => {
    if (!newEmail.trim() || !emailPassword) {
      showError('Email e password sono obbligatori');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      showError('Formato email non valido');
      return;
    }

    setLoading(true);
    try {
      const response = await userProfileService.updateEmail(newEmail.trim(), emailPassword);
      updateLocalUser(response.user);
      showSuccess('Email aggiornata con successo');
      setEditingEmail(false);
      setEmailPassword('');
    } catch (error) {
      showError(error.message || 'Errore nell\'aggiornamento dell\'email');
    } finally {
      setLoading(false);
    }
  };

  // Handler per aggiornamento password
  const handleUpdatePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      showError('Tutti i campi sono obbligatori');
      return;
    }

    if (newPassword !== confirmPassword) {
      showError('Le nuove password non corrispondono');
      return;
    }

    if (newPassword.length < 8) {
      showError('La password deve essere di almeno 8 caratteri');
      return;
    }

    setLoading(true);
    try {
      await userProfileService.updatePassword(currentPassword, newPassword, confirmPassword);
      showSuccess('Password aggiornata con successo');
      setEditingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      showError(error.message || 'Errore nell\'aggiornamento della password');
    } finally {
      setLoading(false);
    }
  };

  // Handler per scaricare i dati dell'utente (GDPR)
  const handleDownloadData = async () => {
    try {
      const [profile, logs] = await Promise.all([
        userProfileService.getProfile(),
        userProfileService.getActivityLogs(1000) // Tutti i log
      ]);

      const data = {
        profile: profile.user,
        activityLogs: logs.logs,
        exportDate: new Date().toISOString()
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mybecco-dati-${user?.username}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      showSuccess('Dati scaricati con successo');
    } catch (error) {
      showError('Errore nel download dei dati');
    }
  };

  return (
    <div className="min-h-screen bg-light-surface dark:bg-dark-surface p-3 sm:p-4 md:p-6 lg:p-8">
      {/* Container centralizzato con max-width ottimizzato per mobile */}
      <div className="max-w-4xl mx-auto space-y-3 sm:space-y-4 md:space-y-6">
        
        {/* Header - Ottimizzato per mobile */}
        <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-xl shadow-lg p-4 sm:p-5 md:p-6
          animate-fade-in opacity-0 transition-all duration-300 hover:shadow-xl"
          style={{ animationDelay: '50ms', animationFillMode: 'forwards' }}>
          <div className="flex items-center gap-2 sm:gap-3">
            <SettingsIcon className="w-7 h-7 sm:w-8 sm:h-8 text-light-secondary dark:text-dark-secondary flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-light-on-surface dark:text-dark-on-surface truncate">
                Impostazioni
              </h2>
              <p className="mt-0.5 text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant truncate">
                Gestisci le tue preferenze e sicurezza
              </p>
            </div>
          </div>
        </div>

        {/* Container Profilo - Nome, Email e Password */}
        <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-xl shadow-lg p-4 sm:p-5 md:p-6
          animate-fade-in opacity-0 transition-all duration-300 hover:shadow-xl"
          style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
          
          {/* Nome e Cognome */}
          <div className="pb-5 border-b border-light-outline-variant dark:border-dark-outline-variant">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-light-on-surface dark:text-dark-on-surface flex items-center gap-2">
              <UserCircle className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">Nome e Cognome</span>
            </h3>
            {!editingName && (
              <button
                onClick={() => setEditingName(true)}
                className="px-3 py-1.5 text-xs sm:text-sm bg-light-secondary dark:bg-dark-secondary text-white rounded-lg hover:opacity-90 transition-all active:scale-95 whitespace-nowrap"
              >
                Modifica
              </button>
            )}
          </div>
          
          {editingName ? (
            <div className="space-y-3">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-light-on-surface-variant dark:text-dark-on-surface-variant mb-1.5">
                  Nome
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full p-2.5 sm:p-3 rounded-lg bg-light-surface dark:bg-dark-surface text-light-on-surface dark:text-dark-on-surface border border-light-outline-variant dark:border-dark-outline-variant focus:ring-2 focus:ring-light-secondary dark:focus:ring-dark-secondary transition-all"
                  placeholder="Inserisci nome"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-light-on-surface-variant dark:text-dark-on-surface-variant mb-1.5">
                  Cognome
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full p-2.5 sm:p-3 rounded-lg bg-light-surface dark:bg-dark-surface text-light-on-surface dark:text-dark-on-surface border border-light-outline-variant dark:border-dark-outline-variant focus:ring-2 focus:ring-light-secondary dark:focus:ring-dark-secondary transition-all"
                  placeholder="Inserisci cognome"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handleUpdateName}
                  disabled={loading}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-light-secondary dark:bg-dark-secondary text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-all active:scale-95 flex-1"
                >
                  <Save className="w-4 h-4" />
                  <span className="text-sm sm:text-base">Salva</span>
                </button>
                <button
                  onClick={() => {
                    setEditingName(false);
                    setFirstName(displayUser.firstName);
                    setLastName(displayUser.lastName);
                  }}
                  className="px-4 py-2.5 bg-light-surface dark:bg-dark-surface text-light-on-surface dark:text-dark-on-surface rounded-lg hover:opacity-90 border border-light-outline-variant dark:border-dark-outline-variant transition-all active:scale-95 flex-1"
                >
                  <span className="text-sm sm:text-base">Annulla</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-base sm:text-lg font-medium text-light-on-surface dark:text-dark-on-surface break-words">
                {displayUser.firstName} {displayUser.lastName}
              </div>
              <div className="text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant break-all">
                Username: @{displayUser.username}
              </div>
            </div>
          )}
        </div>

        {/* Email */}
        <div className="py-5 border-b border-light-outline-variant dark:border-dark-outline-variant">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-light-on-surface dark:text-dark-on-surface flex items-center gap-2">
              <Mail className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">Email</span>
            </h3>
            {!editingEmail && (
              <button
                onClick={() => setEditingEmail(true)}
                className="px-3 py-1.5 text-xs sm:text-sm bg-light-secondary dark:bg-dark-secondary text-white rounded-lg hover:opacity-90 transition-all active:scale-95 whitespace-nowrap"
              >
                Modifica
              </button>
            )}
          </div>

          {editingEmail ? (
            <div className="space-y-3">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-light-on-surface-variant dark:text-dark-on-surface-variant mb-1.5">
                  Nuova Email
                </label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="w-full p-2.5 sm:p-3 rounded-lg bg-light-surface dark:bg-dark-surface text-light-on-surface dark:text-dark-on-surface border border-light-outline-variant dark:border-dark-outline-variant focus:ring-2 focus:ring-light-secondary dark:focus:ring-dark-secondary transition-all"
                  placeholder="nuova@email.com"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-light-on-surface-variant dark:text-dark-on-surface-variant mb-1.5">
                  Conferma con Password
                </label>
                <input
                  type="password"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  className="w-full p-2.5 sm:p-3 rounded-lg bg-light-surface dark:bg-dark-surface text-light-on-surface dark:text-dark-on-surface border border-light-outline-variant dark:border-dark-outline-variant focus:ring-2 focus:ring-light-secondary dark:focus:ring-dark-secondary transition-all"
                  placeholder="Password corrente"
                />
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handleUpdateEmail}
                  disabled={loading}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-light-secondary dark:bg-dark-secondary text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-all active:scale-95 flex-1"
                >
                  <Save className="w-4 h-4" />
                  <span className="text-sm sm:text-base">Salva</span>
                </button>
                <button
                  onClick={() => {
                    setEditingEmail(false);
                    setNewEmail(displayUser.email);
                    setEmailPassword('');
                  }}
                  className="px-4 py-2.5 bg-light-surface dark:bg-dark-surface text-light-on-surface dark:text-dark-on-surface rounded-lg hover:opacity-90 border border-light-outline-variant dark:border-dark-outline-variant transition-all active:scale-95 flex-1"
                >
                  <span className="text-sm sm:text-base">Annulla</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="text-base sm:text-lg font-medium text-light-on-surface dark:text-dark-on-surface break-all">
              {displayUser.email}
            </div>
          )}
        </div>

        {/* Password */}
        <div className="pt-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base sm:text-lg font-semibold text-light-on-surface dark:text-dark-on-surface flex items-center gap-2">
              <Lock className="w-5 h-5 flex-shrink-0" />
              <span className="truncate">Password</span>
            </h3>
            {!editingPassword && (
              <button
                onClick={() => setEditingPassword(true)}
                className="px-3 py-1.5 text-xs sm:text-sm bg-light-secondary dark:bg-dark-secondary text-white rounded-lg hover:opacity-90 transition-all active:scale-95 whitespace-nowrap"
              >
                Modifica
              </button>
            )}
          </div>

          {editingPassword ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-light-on-surface-variant dark:text-dark-on-surface-variant mb-1.5">
                  Password Corrente
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className="w-full p-2.5 sm:p-3 pr-10 rounded-lg bg-light-surface dark:bg-dark-surface text-light-on-surface dark:text-dark-on-surface border border-light-outline-variant dark:border-dark-outline-variant focus:ring-2 focus:ring-light-secondary dark:focus:ring-dark-secondary transition-all"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-light-on-surface-variant dark:text-dark-on-surface-variant hover:text-light-on-surface dark:hover:text-dark-on-surface transition-colors"
                  >
                    {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-light-on-surface-variant dark:text-dark-on-surface-variant mb-1.5">
                  Nuova Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full p-2.5 sm:p-3 pr-10 rounded-lg bg-light-surface dark:bg-dark-surface text-light-on-surface dark:text-dark-on-surface border border-light-outline-variant dark:border-dark-outline-variant focus:ring-2 focus:ring-light-secondary dark:focus:ring-dark-secondary transition-all"
                    placeholder="Min 8 caratteri"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-light-on-surface-variant dark:text-dark-on-surface-variant hover:text-light-on-surface dark:hover:text-dark-on-surface transition-colors"
                  >
                    {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-light-on-surface-variant dark:text-dark-on-surface-variant mt-1">
                  Minimo 8 caratteri
                </p>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-light-on-surface-variant dark:text-dark-on-surface-variant mb-1.5">
                  Conferma Password
                </label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full p-2.5 sm:p-3 pr-10 rounded-lg bg-light-surface dark:bg-dark-surface text-light-on-surface dark:text-dark-on-surface border border-light-outline-variant dark:border-dark-outline-variant focus:ring-2 focus:ring-light-secondary dark:focus:ring-dark-secondary transition-all"
                    placeholder="Ripeti password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-light-on-surface-variant dark:text-dark-on-surface-variant hover:text-light-on-surface dark:hover:text-dark-on-surface transition-colors"
                  >
                    {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              <div className="md:col-span-3 flex flex-col sm:flex-row gap-2 mt-2">
                <button
                  onClick={handleUpdatePassword}
                  disabled={loading}
                  className="flex items-center justify-center gap-2 px-4 py-2.5 bg-light-secondary dark:bg-dark-secondary text-white rounded-lg hover:opacity-90 disabled:opacity-50 transition-all active:scale-95 flex-1"
                >
                  <Save className="w-4 h-4" />
                  <span className="text-sm sm:text-base">Salva Password</span>
                </button>
                <button
                  onClick={() => {
                    setEditingPassword(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="px-4 py-2.5 bg-light-surface dark:bg-dark-surface text-light-on-surface dark:text-dark-on-surface rounded-lg hover:opacity-90 border border-light-outline-variant dark:border-dark-outline-variant transition-all active:scale-95 flex-1 sm:flex-initial"
                >
                  <span className="text-sm sm:text-base">Annulla</span>
                </button>
              </div>
            </div>
          ) : (
            <div className="text-base sm:text-lg text-light-on-surface-variant dark:text-dark-on-surface-variant">
              ••••••••
            </div>
          )}
        </div>
        </div>

        {/* Container Notifiche e Tema */}
        <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-xl shadow-lg p-4 sm:p-5 md:p-6
          animate-fade-in opacity-0 transition-all duration-300 hover:shadow-xl"
          style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
          
          {/* Notifiche */}
          <div className="pb-5 border-b border-light-outline-variant dark:border-dark-outline-variant">
            <div className="flex items-start gap-3 text-light-on-surface dark:text-dark-on-surface">
              <Bell className="w-5 h-5 flex-shrink-0 mt-0.5 text-light-secondary dark:text-dark-secondary" />
              <div className="min-w-0 flex-1">
                <h4 className="font-semibold text-base sm:text-lg">Notifiche</h4>
                <p className="text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant mt-1.5 leading-relaxed">
                  Gestisci le preferenze di notifica per rimanere aggiornato su eventi, messaggi e attività importanti.
                </p>
                <p className="text-xs text-light-on-surface-variant dark:text-dark-on-surface-variant mt-2 italic opacity-75">Funzionalità in fase di sviluppo</p>
              </div>
            </div>
          </div>

        {/* Tema */}
        <div className="pt-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <Sun className="w-5 h-5 text-light-on-surface dark:text-dark-on-surface flex-shrink-0" />
              <h3 className="text-base sm:text-lg font-semibold text-light-on-surface dark:text-dark-on-surface truncate">
                Tema
              </h3>
            </div>
            
            {/* Toggle Switch Mobile-Optimized */}
            <div className="flex items-center gap-2 sm:gap-3">
              <Sun className="w-4 h-4 sm:w-5 sm:h-5 text-light-on-surface-variant dark:text-dark-on-surface-variant flex-shrink-0" />
              <button
                onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                className="relative w-12 h-6 sm:w-14 sm:h-7 bg-light-surface dark:bg-dark-surface rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-light-secondary dark:focus:ring-dark-secondary active:scale-95"
              >
                <span
                  className={`absolute top-0.5 left-0.5 w-5 h-5 sm:w-6 sm:h-6 bg-light-surface-container dark:bg-dark-surface-container rounded-full shadow-lg transform transition-transform duration-300 flex items-center justify-center ${
                    theme === 'dark' ? 'translate-x-6 sm:translate-x-7' : 'translate-x-0'
                  }`}
                >
                  {theme === 'dark' ? (
                    <Moon className="w-3 h-3 sm:w-4 sm:h-4 text-dark-secondary" />
                  ) : (
                    <Sun className="w-3 h-3 sm:w-4 sm:h-4 text-light-secondary" />
                  )}
                </span>
              </button>
              <Moon className="w-4 h-4 sm:w-5 sm:h-5 text-light-on-surface-variant dark:text-dark-on-surface-variant flex-shrink-0" />
            </div>
          </div>
        </div>
        </div>

        {/* Privacy e GDPR */}
        <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-xl shadow-lg p-4 sm:p-5 md:p-6
          animate-fade-in opacity-0 transition-all duration-300 hover:shadow-xl"
          style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <Shield className="w-5 h-5 text-light-secondary dark:text-dark-secondary flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <h4 className="font-semibold text-base sm:text-lg text-light-on-surface dark:text-dark-on-surface">
                  Privacy e Sicurezza
                </h4>
                <p className="text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant mt-1">
                  Scarica una copia dei tuoi dati personali e log di attività (GDPR).
                </p>
              </div>
            </div>
            <button
              onClick={handleDownloadData}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-light-secondary dark:bg-dark-secondary text-white rounded-lg hover:opacity-90 transition-all active:scale-95 whitespace-nowrap"
            >
              <Download className="w-4 h-4" />
              <span className="text-sm">Scarica Dati</span>
            </button>
          </div>
        </div>

        {/* Info Sistema */}
        <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-xl shadow-lg p-4 sm:p-5 md:p-6
          animate-fade-in opacity-0 transition-all duration-300 hover:shadow-xl"
          style={{ animationDelay: '350ms', animationFillMode: 'forwards' }}>
          <div className="flex items-start gap-3 text-light-on-surface dark:text-dark-on-surface">
            <Info className="w-5 h-5 text-light-secondary dark:text-dark-secondary flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <h4 className="font-semibold text-base sm:text-lg">Info Sistema</h4>
              <p className="text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant mt-1.5">MyBecco v1.0.0</p>
              <p className="text-xs text-light-on-surface-variant dark:text-dark-on-surface-variant mt-1">© 2025 - Tutti i diritti riservati</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
